/**
 * useThreadSummaries
 *
 * Subscribes to smsThreads/{phone10} docs for an array of phones.
 * Returns real-time thread metadata (last message preview, timestamp, direction).
 *
 * @param phones - Array of phone strings (E.164 or raw digits)
 * @returns Map of phone -> ThreadSummary, plus loading state
 */
import { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';
import { useOrganization } from '../../contexts/OrganizationContext';
import type { ThreadSummary } from '../../components/shared/SmsExchange/types';

/** Normalize phone to last 10 digits (same logic as useDialpadSms). */
const normalize10 = (phone: string): string =>
  phone.replace(/\D/g, '').slice(-10);

export function useThreadSummaries(phones: string[]): {
  summaries: Map<string, ThreadSummary>;
  loading: boolean;
} {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId;

  const [summaries, setSummaries] = useState<Map<string, ThreadSummary>>(new Map());
  const [loading, setLoading] = useState(true);

  // Stable key to avoid re-subscribing on every render
  const phonesKey = phones
    .map(normalize10)
    .filter((p) => p.length === 10)
    .sort()
    .join(',');

  // Track pending initial loads
  const pendingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!orgId || !phonesKey) {
      setSummaries(new Map());
      setLoading(false);
      return;
    }

    const normalized = phonesKey.split(',').filter(Boolean);
    pendingRef.current = new Set(normalized);
    setLoading(true);
    setSummaries(new Map());

    const unsubs = normalized.map((phone10) => {
      const threadRef = doc(db, 'organizations', orgId, COLLECTIONS.SMS_THREADS, phone10);

      return onSnapshot(
        threadRef,
        (snap) => {
          setSummaries((prev) => {
            const next = new Map(prev);
            if (snap.exists()) {
              const d = snap.data();
              next.set(phone10, {
                lastMessageText: (d.lastMessageText || '').slice(0, 200),
                lastMessageAt: d.lastMessageAt || null,
                lastMessageDirection: d.lastMessageDirection || 'inbound',
              });
            }
            return next;
          });

          pendingRef.current.delete(phone10);
          if (pendingRef.current.size === 0) {
            setLoading(false);
          }
        },
        (err) => {
          console.error('[useThreadSummaries] listener error', err);
          pendingRef.current.delete(phone10);
          if (pendingRef.current.size === 0) {
            setLoading(false);
          }
        }
      );
    });

    return () => unsubs.forEach((unsub) => unsub());
  }, [orgId, phonesKey]);

  return { summaries, loading };
}
